import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const TIER_FEATURES = {
  free: {
    adFree: false,
    themes: ['classic'],
    dailyPowerUps: { undo: 0, bomb: 0, refresh: 0 },
    weeklyChallenge: false,
    exclusiveEffects: false,
  },
  premium: {
    adFree: true,
    themes: ['classic', 'ocean', 'sunset', 'neon'],
    dailyPowerUps: { undo: 3, bomb: 1, refresh: 1 },
    weeklyChallenge: false,
    exclusiveEffects: false,
  },
  premium_plus: {
    adFree: true,
    themes: ['classic', 'ocean', 'sunset', 'neon', 'galaxy', 'retro', 'forest', 'candy'],
    dailyPowerUps: { undo: 5, bomb: 3, refresh: 3 },
    weeklyChallenge: true,
    exclusiveEffects: true,
  },
};

const EntitlementContext = createContext(null);

export function EntitlementProvider({ children }) {
  const [tier, setTier] = useState('free');
  const [adFree, setAdFree] = useState(false);
  const [consumables, setConsumables] = useState({ undos: 0, bombs: 0, refreshes: 0 });
  const [loading, setLoading] = useState(true);

  const features = TIER_FEATURES[tier] || TIER_FEATURES.free;
  const showAds = !adFree && !features.adFree;

  const isPremium = tier === 'premium' || tier === 'premium_plus';
  const isPremiumPlus = tier === 'premium_plus';
  const isAdFree = adFree || features.adFree;

  const canUseTheme = useCallback((themeId) => {
    return features.themes.includes(themeId);
  }, [features.themes]);

  const refresh = useCallback(async () => {
    try {
      // Try to fetch from API
      const { getEntitlements } = await import('../services/api');
      const data = await getEntitlements();
      if (data) {
        setTier(data.tier || 'free');
        setAdFree(data.adFree || false);
        setConsumables(data.consumables || { undos: 0, bombs: 0, refreshes: 0 });
        // Cache to localStorage
        localStorage.setItem('bb_entitlements', JSON.stringify(data));
      }
    } catch {
      // Fallback to cached data
      try {
        const cached = JSON.parse(localStorage.getItem('bb_entitlements') || '{}');
        if (cached.tier) {
          setTier(cached.tier);
          setAdFree(cached.adFree || false);
          setConsumables(cached.consumables || { undos: 0, bombs: 0, refreshes: 0 });
        }
      } catch {
        // Use defaults
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load cached first for instant display, then refresh from API
    try {
      const cached = JSON.parse(localStorage.getItem('bb_entitlements') || '{}');
      if (cached.tier) {
        setTier(cached.tier);
        setAdFree(cached.adFree || false);
        setConsumables(cached.consumables || { undos: 0, bombs: 0, refreshes: 0 });
      }
    } catch {
      // Use defaults
    }
    refresh();
  }, [refresh]);

  const value = {
    tier,
    adFree: isAdFree,
    showAds,
    availableThemes: features.themes,
    dailyPowerUps: features.dailyPowerUps,
    weeklyChallenge: features.weeklyChallenge,
    exclusiveEffects: features.exclusiveEffects,
    consumables,
    loading,
    refresh,
    isPremium,
    isPremiumPlus,
    isAdFree,
    canUseTheme,
  };

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlements() {
  const context = useContext(EntitlementContext);
  if (!context) {
    // Return safe defaults if used outside provider (e.g., guest mode)
    return {
      tier: 'free',
      adFree: false,
      showAds: true,
      availableThemes: ['classic'],
      dailyPowerUps: { undo: 0, bomb: 0, refresh: 0 },
      weeklyChallenge: false,
      exclusiveEffects: false,
      consumables: { undos: 0, bombs: 0, refreshes: 0 },
      loading: false,
      refresh: () => {},
      isPremium: false,
      isPremiumPlus: false,
      isAdFree: false,
      canUseTheme: (id) => id === 'classic',
    };
  }
  return context;
}
